import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

import { masterChat, type ChatMessage } from '@/api/client';
import RichText from '@/components/RichText';
import { useAudio } from '@/hooks/useAudio';

/**
 * Master's Room — free-form conversation with the AI Tarot Master.
 *
 * Style: candlelit chamber. Messages float in like spoken thoughts. The
 * Master's bubble is left-aligned in gold serif, user's bubble is
 * right-aligned in night-700.
 *
 * Free quota = 3 turns/day (enforced backend-side); subscription unlocks
 * unlimited (TODO week 2).
 */
export default function Master() {
  const { t } = useTranslation();
  const audio = useAudio();

  const [messages, setMessages] = useState<ChatMessage[]>([{
    role: 'assistant',
    content: t('master.greeting'),
  }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    audio.ambient(true);
    return () => audio.ambient(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setError(null);
    const userTurn: ChatMessage = { role: 'user', content: text };
    setMessages((m) => [...m, userTurn]);
    setSending(true);
    try {
      const { reply } = await masterChat(
        [...messages, userTurn].slice(-8),
        text,
      );
      audio.play('bell');
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto flex max-w-md flex-col" style={{ minHeight: 'calc(100vh - 80px)' }}>
      <header className="px-5 pt-2">
        <h1 className="text-center font-serif text-2xl text-gold-300">
          {t('master.title')}
        </h1>
        <p className="mt-1 text-center text-xs italic text-gold-300/60">
          {t('master.subtitle')}
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={`mb-4 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={
                  m.role === 'user'
                    ? 'max-w-[80%] rounded-2xl rounded-br-sm bg-night-700 px-4 py-2 text-gold-200'
                    : 'max-w-[85%] rounded-2xl rounded-bl-sm bg-night-800/60 px-4 py-3 ' +
                      'font-serif italic leading-relaxed text-gold-200 ' +
                      'border border-gold-500/20 backdrop-blur-sm'
                }
              >
                {m.role === 'assistant' && (
                  <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-widest text-gold-500/70">
                    <span>✦</span>
                    <span>{t('master.label')}</span>
                  </div>
                )}
                {/* Assistant replies sometimes contain **bold** markdown
                    (Gemini likes to bold key terms). Render through
                    RichText so the user sees proper formatting instead
                    of literal asterisks. User messages stay plain. */}
                {m.role === 'assistant' ? (
                  <RichText text={m.content} />
                ) : (
                  m.content
                )}
              </div>
            </motion.div>
          ))}
          {sending && (
            <motion.div
              key="typing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-4 flex justify-start"
            >
              <div className="flex items-center gap-1 rounded-2xl border border-gold-500/20 bg-night-800/60 px-4 py-3">
                <span className="h-2 w-2 animate-pulse rounded-full bg-gold-400" />
                <span
                  className="h-2 w-2 animate-pulse rounded-full bg-gold-400"
                  style={{ animationDelay: '0.15s' }}
                />
                <span
                  className="h-2 w-2 animate-pulse rounded-full bg-gold-400"
                  style={{ animationDelay: '0.3s' }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {error && (
          <p className="mb-2 text-center text-xs text-rose-400">{error}</p>
        )}
        <div ref={endRef} />
      </div>

      <form
        className="sticky bottom-0 border-t border-gold-500/20 bg-night-900/80 px-5 py-3 backdrop-blur"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('master.placeholder')}
            maxLength={600}
            className="flex-1 rounded-full border border-gold-500/30 bg-night-800
                       px-4 py-2 text-gold-200 outline-none focus:border-gold-400"
            disabled={sending}
          />
          <button
            type="submit"
            className="btn-primary px-4 py-2 text-sm disabled:opacity-40"
            disabled={!input.trim() || sending}
          >
            ✦
          </button>
        </div>
        <p className="mt-1 text-center text-[10px] text-gold-300/40">
          {t('master.free_quota')}
        </p>
      </form>
    </main>
  );
}
